import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send } from 'lucide-react';
import { chatService } from '@/services/chatService';
import { ChatMessage, ChatThread } from '@/types/chatTypes';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { format, isValid, isToday, isYesterday } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Timestamp } from 'firebase/firestore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';

interface ChatWindowProps {
  threadId: string;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ threadId }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const pendingMessageId = useRef<string | null>(null);

  const formatMessageTime = (timestamp: string | number | Date | null) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return `${days}d ago`;
    } else {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours > 0) {
        return `${hours}h ago`;
      } else {
        const minutes = Math.floor(diff / (1000 * 60));
        if (minutes > 0) {
          return `${minutes}m ago`;
        } else {
          return 'Just now';
        }
      }
    }
  };

  useEffect(() => {
    if (!user || !threadId) return;

    const loadThreadAndMessages = async () => {
      try {
        // Load thread data
        const threads = await chatService.getChatThreads(user.id, user.role as 'student' | 'instructor');
        const currentThread = threads.find(t => t.id === threadId);
        if (currentThread) {
          setThread(currentThread);
        } else {
          navigate('/chat');
          return;
        }

        // Load messages
        const threadMessages = await chatService.getMessages(threadId);
        setMessages(threadMessages);
        setLoading(false);
      } catch (error) {
        console.error('Error loading thread and messages:', error);
        setLoading(false);
      }
    };

    loadThreadAndMessages();

    // Subscribe to new messages
    const unsubscribe = chatService.subscribeToMessages(threadId, (newMessages) => {
      setMessages(prevMessages => {
        // Filter out any pending messages that have been confirmed
        const filteredMessages = prevMessages.filter(msg => msg.id !== pendingMessageId.current);
        // Add new messages, avoiding duplicates
        const uniqueMessages = [...filteredMessages];
        newMessages.forEach(newMsg => {
          if (!uniqueMessages.some(msg => msg.id === newMsg.id)) {
            uniqueMessages.push(newMsg);
          }
        });
        // Sort messages by timestamp
        return uniqueMessages.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      });
      pendingMessageId.current = null;
    });

    return () => {
      unsubscribe();
    };
  }, [threadId, user, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || !thread) return;

    try {
      const messageId = Date.now().toString();
      pendingMessageId.current = messageId;

      const message: ChatMessage = {
        id: messageId,
        senderId: user.id,
        senderName: `${user.firstName} ${user.lastName}`,
        senderRole: user.role as 'student' | 'instructor',
        receiverId: user.role === 'student' ? thread.participants.instructor.id : thread.participants.student.id,
        receiverName: user.role === 'student' ? thread.participants.instructor.name : thread.participants.student.name,
        receiverRole: user.role === 'student' ? 'instructor' : 'student',
        content: newMessage.trim(),
        timestamp: new Date().toISOString(),
        read: false,
        enrollmentId: thread.enrollmentId
      };

      // Add message to local state immediately
      setMessages(prev => [...prev, message]);
      setNewMessage('');

      // Send message to server
      await chatService.sendMessage(threadId, message);
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the pending message if sending failed
      setMessages(prev => prev.filter(msg => msg.id !== pendingMessageId.current));
      pendingMessageId.current = null;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/4 bg-gray-200 rounded" />
                  <div className="h-4 w-3/4 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b bg-muted/50 p-4">
        <CardTitle className="flex items-center gap-3 text-lg">
          <Avatar className="h-10 w-10">
            <AvatarImage src={`https://ui-avatars.com/api/?name=${thread?.participants[user?.role === 'student' ? 'instructor' : 'student'].name}`} />
            <AvatarFallback>
              {thread?.participants[user?.role === 'student' ? 'instructor' : 'student'].name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span>{thread?.participants[user?.role === 'student' ? 'instructor' : 'student'].name}</span>
            <span className="text-sm text-muted-foreground">
              {user?.role === 'student' ? 'Instructor' : 'Student'}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-[calc(100vh-16rem)] md:h-[calc(100vh-12rem)] px-4">
          <div className="space-y-4 py-4">
          {messages.map((message, index) => {
              const isCurrentUser = message.senderId === user?.id;
              const showDate =
                index === 0 ||
                new Date(message.timestamp).toDateString() !==
                new Date(messages[index - 1].timestamp).toDateString();

            return (
                <div key={message.id} className="space-y-4">
                  {showDate && (
                  <div className="flex justify-center">
                      <div className="bg-muted px-3 py-1 rounded-full text-sm text-muted-foreground">
                        {new Date(message.timestamp).toLocaleDateString()}
                      </div>
                  </div>
                )}
                <div
                    className={`flex ${
                      isCurrentUser ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        isCurrentUser
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm break-words">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          isCurrentUser
                            ? "text-primary-foreground/80"
                            : "text-muted-foreground"
                        }`}
                      >
                        {formatMessageTime(message.timestamp)}
                      </p>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        </ScrollArea>
      </CardContent>
      <div className="p-4 border-t mt-auto">
        <div className="flex gap-2">
          <Textarea
            placeholder="Type a message..."
            className="flex-1 min-h-[60px] max-h-[120px]"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <Button
            className="self-end"
            size="icon"
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}; 